// What became due, and in what order. Pure.
//
// ICD-S05 §7.3: A JOB IS KEYED BY WHEN IT WAS DUE, NOT BY WHEN THE SWEEP
// WOKE. This function takes a half-open range and answers what fell inside it,
// so a sweep waking at 14:03 for work due at 14:00 finds it, and a sweep
// waking after a two-day outage finds two days of work in the order it was due
// rather than skipping to now. That is the whole of catch-up, and it is the
// reason planning is a function of a range instead of a function of `now`.
//
// A JOB ID IS DERIVED AND CARRIES NO CLOCK. `<leagueId>|<kind>|<dueAt>` and
// nothing else, so planning the same moment twice names the same job — which
// is what lets the ledger's primary key do the deduplication and lets a claim
// be a conditional insert. An id with a timestamp or a random part in it would
// make every replan a new job and every retry a duplicate.

import {
  LINEUP_LEAD_MINUTES,
  type JobKindId,
  type LeagueSettings,
  type ScheduledJobShape,
} from '@fsp/domain';
import type { Game } from '@fsp/sports-core';
import type { StoredPeriod } from './calendar';
import { firstLockAt, gamesInWindow, lockFromKickoff, periodKind, waiverRunsBetween } from './read';
import { wallClocksBetween } from './zone';

/**
 * Within one instant, the order work happens in. §7.6.
 *
 * Scoring a matchup before the lineups it scores were locked would score a
 * lineup somebody changed after kickoff, so this is not a display preference.
 */
export const KIND_ORDER: readonly JobKindId[] = [
  'period.open',
  // After the week opens, so a bot sets the lineup the carry just wrote, and
  // before a lock at the same instant, which it would otherwise be too late for.
  'bots.lineup',
  'lineup.lock',
  // After a lock at the same instant, so a warning never names a slot that
  // has just stopped being anybody's to change.
  'notify.lineup.warn',
  'notify.lineup.last',
  'matchup.score',
  'stats.correct',
  'matchup.finalize',
  // After the scores at the same instant: a landing plans both, and the cue
  // reads the lines the pass just scored.
  'league.versus.refresh',
  'waivers.process',
  // After the run, so a bot looking at the wire sees who cleared it.
  'bots.checkin',
  'trades.advance',
  'trades.deadline',
  'rules.vote.close',
  'notify.draft.plan',
  'notify.draft.remind',
  'draft.start',
  // AFTER THE DRAFT, in the same instant: the schedule is drawn over the
  // teams the draft just filled, and a league only opens once it has rosters.
  // After `period.open` too, which at a period's start idles on a league still
  // in `drafting` — `openSeason` moves the status itself.
  'season.open',
  'draft.pick.expire',
  'period.close',
  'season.rollover',
];

/**
 * The kinds whose work leaves everything the planner reads as it was: they
 * write lineups, scores, notices and the matchup cue's table, and never the
 * league's status, rules or bots, its calendar, or a trade's, a vote's or the
 * draft's instants. A run of one that did something (`ok`) does not have its
 * league planned again (ICD-S05 §8, check 50).
 *
 * BY KIND, NOT BY OUTCOME (2026-10-22, PLAN-2026-10-10 §4b). Every `ok` job
 * asked for a plan, and a league's locks, scores and cue refreshes were most
 * of its plans: 85 in production's week across two leagues, each a job's
 * ledger rows written to learn nothing had changed. A kind missing from here
 * plans again, so a new one is safe until somebody shows it belongs.
 */
export const PLANS_NOTHING: ReadonlySet<JobKindId> = new Set<JobKindId>([
  'lineup.lock',
  'bots.lineup',
  'notify.lineup.warn',
  'notify.lineup.last',
  'matchup.score',
  'league.versus.refresh',
]);

/** Where a kind falls within one instant. A kind missing from the order sorts last. */
export const kindRank = (kind: JobKindId): number => {
  const at = KIND_ORDER.indexOf(kind);
  return at === -1 ? KIND_ORDER.length : at;
};
const rank = kindRank;

/**
 * How long after a period ends we look for a corrected stat line.
 *
 * Two days, because the provider corrections this exists for arrive on the
 * Tuesday after a Sunday. It is a constant rather than a setting because it is
 * a fact about the source, not a rule a commissioner would change — and
 * CLAUDE.md's "rules are data" is about the second kind.
 */
export const CORRECTION_DELAY_MS = 48 * 60 * 60 * 1000;

/**
 * When a draft's two reminders go out, as leads on the draft time.
 *
 * A day and an hour for "it is nearly time", three days for "you have not
 * ranked anybody". Three days is the one worth arguing about and it is the
 * point of that message: it has to land while there is still an evening left
 * to do something about it, and a reminder the morning of a draft is a
 * reminder that somebody's team will be autopicked.
 */
export const DRAFT_REMINDERS_MS = [24 * 60 * 60 * 1000, 60 * 60 * 1000] as const;
export const PLAN_REMINDER_MS = 72 * 60 * 60 * 1000;

/**
 * The lineup warning's leads on the week's first lock: one per choice a
 * manager has (`LINEUP_LEAD_MINUTES`, NOTIFICATIONS.md §4), and the last call
 * ten minutes before it, which fires only if a slot still cannot score.
 */
export const LINEUP_WARN_LEADS_MS = LINEUP_LEAD_MINUTES.map((m) => Number(m) * 60_000);
export const LINEUP_LAST_CALL_MS = 10 * 60_000;

/**
 * THE BOTS' TWO TIMES (B-292). Their lineup an hour before each kickoff that
 * locks somebody: late enough for the morning's injury news, early enough that
 * a pickup it must make still lands before the game. And their daily look at
 * eight in the morning in the league's zone: after a waiver run, which is
 * small hours by default, and before any game. Constants, because they are how
 * the house's bots behave and not a rule a commissioner sets.
 */
export const BOT_LINEUP_LEAD_MS = 60 * 60_000;
export const BOT_CHECKIN_TIME = '08:00';

export interface PlanInput {
  leagueId: string;
  settings: LeagueSettings;
  periods: readonly StoredPeriod[];
  games: readonly Game[];
  /** Exclusive. */
  since: Date;
  /** Inclusive. */
  now: Date;
  /** Only kinds with a live handler are planned. See `JobHandler`. */
  kinds: ReadonlySet<JobKindId>;
  /** Whether a bot plays any seat (`ClockLeague.bots`): their jobs are planned only then. */
  bots?: boolean;
}

const ms = (at: string): number => Date.parse(at);
const iso = (at: number): string => new Date(at).toISOString();

export function jobId(leagueId: string, kind: JobKindId, dueAt: string): string {
  return `${leagueId}|${kind}|${dueAt}`;
}

/** Everything that became due in `(since, now]`, in the order it must run. */
export function planJobs(input: PlanInput): ScheduledJobShape[] {
  const { leagueId, settings, periods, games, kinds } = input;
  const after = input.since.getTime();
  const until = input.now.getTime();
  const out: ScheduledJobShape[] = [];
  const planned = new Set<string>();

  const add = (
    kind: JobKindId,
    dueMs: number,
    period: number | null,
    windowId: string | null,
  ): void => {
    if (!kinds.has(kind)) return;
    if (dueMs <= after || dueMs > until) return;
    const dueAt = iso(dueMs);
    const id = jobId(leagueId, kind, dueAt);
    // ONE JOB PER ID. A draft set for the instant a period starts plans
    // `season.open` twice under one id, and the second would be a claim
    // racing the first inside the same sweep.
    if (planned.has(id)) return;
    planned.add(id);
    out.push({ id, leagueId, kind, dueAt, period, windowId });
  };

  const regular = periods.filter((p) => periodKind(settings, p.index) !== 'off');
  const last = regular.length > 0 ? regular[regular.length - 1] : undefined;

  for (const period of periods) {
    add('period.open', ms(period.start), period.index, null);
    // THE SEASON OPENS AT THE DRAFT (below), AND IS LOOKED AT AGAIN EVERY
    // WEEK. The weekly one is what opens a league nobody's draft job drafted
    // — a draft run by hand, or one whose own job failed for longer than the
    // retry reaches — and it is an idle row for every league already open.
    if (periodKind(settings, period.index) !== 'off') {
      add('season.open', ms(period.start), period.index, null);
    }
    add('period.close', ms(period.end), period.index, null);
    add('matchup.finalize', ms(period.end), period.index, null);
    add('stats.correct', ms(period.end) + CORRECTION_DELAY_MS, period.index, null);

    for (const window of period.windows) {
      add('matchup.score', ms(window.end), period.index, window.id);

      // One lock job per distinct instant at which something in this window
      // stops being editable. Under `per-player-game` that is every kickoff,
      // because each one locks its own slots; under the other two rules it is
      // the single window lock. The handler is a conditional UPDATE either
      // way, so the extra rows cost a few statements and buy the lineup being
      // versioned AT the lock rather than at the end of the week.
      const instants = new Set<number>();
      const windowLock = lockFromKickoff(settings, window.firstKickoff);
      if (windowLock !== null) instants.add(ms(windowLock));
      if (settings.lineup.lockRule === 'per-player-game') {
        for (const game of gamesInWindow(window, games)) instants.add(ms(game.start));
        // AND THE END OF THE WINDOW, WHICH IS THE LOCK A PRO BYE NEEDS. A
        // player whose club is not playing this week has no kickoff, so under
        // a per-player rule nothing would ever stamp his slot and last week's
        // lineup would still be editable in week ten. The week ending is the
        // moment after which nothing moves, and it locks whatever is left.
        // Found 2026-09-19, when narrowing the lock to its own players stopped
        // the first kickoff from covering this by accident.
        instants.add(ms(window.end));
      }
      for (const at of [...instants].sort((a, b) => a - b)) {
        add('lineup.lock', at, period.index, window.id);
      }

      // THE BOTS' LINEUP, before each of those that is a kickoff — not the
      // week's end, which only locks what is left and has nothing to set for.
      if (input.bots === true && periodKind(settings, period.index) !== 'off') {
        for (const at of instants) {
          if (at === ms(window.end) && settings.lineup.lockRule === 'per-player-game') continue;
          add('bots.lineup', at - BOT_LINEUP_LEAD_MS, period.index, window.id);
        }
      }

      // THE LINEUP WARNING (D3), on the week's first lock: a job per lead a
      // manager can choose, each telling those who chose it, and the last
      // call. A week the league does not play has nothing to warn about.
      const firstLock =
        periodKind(settings, period.index) === 'off' ? null : firstLockAt(settings, window, games);
      if (firstLock !== null) {
        for (const lead of LINEUP_WARN_LEADS_MS) {
          add('notify.lineup.warn', firstLock - lead, period.index, window.id);
        }
        add('notify.lineup.last', firstLock - LINEUP_LAST_CALL_MS, period.index, window.id);
      }
    }
  }

  const deadline = settings.trades.deadlinePeriod;
  if (deadline !== null) {
    const at = periods.find((p) => p.index === deadline);
    if (at) add('trades.deadline', ms(at.end), deadline, null);
  }

  // ONLY THE AUTOPICK DRAFT IS THE CLOCK'S (settings version 3, BACKLOG D1).
  // Its reminders send each manager to their ranking list, and at its instant
  // the clock drafts. A draft held in person and entered by the commissioner
  // (`manual`) has no list to fill and nothing to run at its time: it ends
  // when the commissioner finishes entering it, and that finish builds the
  // calendar and opens the season itself (`draft/manual`); the weekly
  // `season.open` above, planned from that calendar, opens a league whose
  // finish could not.
  //
  // A `LIVE` DRAFT IS ITS ROOM'S, and the clock only knocks (D10): the day
  // and the hour before, a reminder to every manager, and at the time a
  // `draft.start` that asks the room to begin even if nobody has opened it.
  // No "you have ranked nobody" — the room is where a live drafter gets
  // ready — and no `season.open` at the time, since the room's finish opens
  // the season when the last pick is in.
  if (settings.draft.scheduledAt && settings.draft.mode === 'live') {
    const at = ms(settings.draft.scheduledAt);
    for (const lead of DRAFT_REMINDERS_MS) add('notify.draft.remind', at - lead, null, null);
    add('draft.start', at, null, null);
  }
  if (settings.draft.scheduledAt && settings.draft.mode === 'autopick') {
    const at = ms(settings.draft.scheduledAt);
    // THE REMINDERS, AND THEIR LEAD TIMES ARE CONSTANTS. K3 reads a per-person
    // lead time from preferences for the LINEUP warning, because that one is
    // about a weekly habit somebody has an opinion about. A draft happens once
    // a year on a date the league agreed, and "three days" and "a day" and "an
    // hour" are what every platform sends; a setting here would be a question
    // nobody wants asked.
    add('notify.draft.plan', at - PLAN_REMINDER_MS, null, null);
    for (const lead of DRAFT_REMINDERS_MS) add('notify.draft.remind', at - lead, null, null);
    add('draft.start', at, null, null);
    add('season.open', at, null, null);
  }

  // A RUN AT THE LEAGUE'S PROCESSING TIME, and none for a league with no
  // waivers (rule 9, check 32): its processing time is a field every league
  // carries, and planning from it alone put an idle row on the ledger every
  // week for a league that had chosen first come, first served. A continuous
  // schedule decides each player as he clears: its runs are the instants its
  // claims decide (the waiver handler's `instants`, B-523), not the calendar.
  //
  // AND A LATE WEEK'S RUN WHERE IT MOVED (`runHappensAt`, B-507): a week
  // whose games run past Wednesday's run moves it to Thursday or Friday, so
  // the times are read from a week before the range, whose run can land in it.
  if (settings.waivers.system !== 'none' && settings.waivers.processing.schedule !== 'continuous') {
    for (const at of waiverRunsBetween(settings, periods, input.since, input.now)) {
      add('waivers.process', at, null, null);
    }
  }

  if (last) {
    add('season.rollover', ms(last.end), null, null);
    // AND AGAIN ONCE THE LAST WEEK'S CORRECTION PASS HAS RUN. A final the
    // end's settle could not declare (its job refused, which is never tried
    // again) is declared by that pass, after the rollover above found it
    // unplayed and was refused too; this one concludes the season. Idle for a
    // season the first one concluded.
    add('season.rollover', ms(last.end) + CORRECTION_DELAY_MS, null, null);
  }

  // THE BOTS' DAILY LOOK, from the first week the league plays to the end of
  // its last: a bot has nothing to look at before its season or after it.
  const first = regular[0];
  if (input.bots === true && first !== undefined && last !== undefined) {
    for (const at of wallClocksBetween(
      settings.identity.timeZone,
      input.since,
      input.now,
      BOT_CHECKIN_TIME,
      null,
    )) {
      const t = at.getTime();
      if (t < ms(first.start) || t >= ms(last.end)) continue;
      const period = regular.find((p) => ms(p.start) <= t && t < ms(p.end));
      add('bots.checkin', t, period?.index ?? null, null);
    }
    // AND ONE AT THE INSTANT THE LAST WEEK ENDS: after the final is made final
    // and before the season closes (`KIND_ORDER`), so a bot that won it, or
    // lost it, says so (B-297). A closed league plans nothing, so the morning
    // after would be too late.
    add('bots.checkin', ms(last.end), last.index, null);
  }

  return out.sort((a, b) => ms(a.dueAt) - ms(b.dueAt) || rank(a.kind) - rank(b.kind));
}

/**
 * The scoring passes the landing of a week's lines asks for. BACKLOG L7.
 *
 * THE WEEK'S SCORES MOVE WHEN ITS LINES LAND, not only when its window ends
 * (B-130). A real feed's lines arrive hours after each slate — nflverse's once
 * a day — and a scoreboard that waited for the window's end showed nothing
 * from Thursday night to Tuesday morning. A landing inside a window asks for a
 * pass over that window's period, due at the landing's instant; one outside
 * every window (between seasons) or in an `off` week asks for nothing, and a
 * line that lands after its week has been declared is `stats.correct`'s.
 *
 * NOT A RANGE, unlike `planJobs`, and that is the point of it being separate.
 * A landing's instant is the run's own `now` — when it STARTED — and a sweep
 * firing at that same minute (the daily ingest and the minute sweep share
 * 09:00 in production) reads the run while it is still `running`. The next
 * sweep's range starts after that instant, so a range would miss it every
 * day. A pass stays due until it has run, the way a failed job does; `due()`
 * drops the ones the ledger has settled.
 *
 * AND THE MATCHUP CUE'S REFRESH (DATA_LAYER_DESIGN §4.3, Phase 1): a landing
 * inside any week the league plays — a window or not, since a correction
 * run lands between them — asks for one `league.versus.refresh` at the
 * landing's instant, when the deployment has a handler for it. The table
 * rests on the season's lines, and the landing is when they moved.
 */
export function scoringPasses(
  leagueId: string,
  settings: LeagueSettings,
  periods: readonly StoredPeriod[],
  landings: readonly string[],
  kinds: ReadonlySet<JobKindId>,
): ScheduledJobShape[] {
  const scores = kinds.has('matchup.score');
  const cue = kinds.has('league.versus.refresh');
  if (!scores && !cue) return [];
  const out = new Map<string, ScheduledJobShape>();
  for (const at of landings) {
    const t = ms(at);
    for (const period of periods) {
      if (periodKind(settings, period.index) === 'off') continue;
      const dueAt = iso(t);
      if (cue && ms(period.start) <= t && t < ms(period.end)) {
        const id = jobId(leagueId, 'league.versus.refresh', dueAt);
        out.set(id, {
          id,
          leagueId,
          kind: 'league.versus.refresh',
          dueAt,
          period: null,
          windowId: null,
        });
      }
      if (!scores) continue;
      const window = period.windows.find((w) => ms(w.start) <= t && t < ms(w.end));
      if (window === undefined) continue;
      const id = jobId(leagueId, 'matchup.score', dueAt);
      out.set(id, {
        id,
        leagueId,
        kind: 'matchup.score',
        dueAt,
        period: period.index,
        windowId: window.id,
      });
    }
  }
  return [...out.values()];
}
